import { useCallback, useEffect, ReactNode } from 'react';
import { useTracker, ItemState } from '@/contexts/tracker-context';

export interface OotItemChildProps {
  item: ItemState;
  updateItem: (updates: Partial<ItemState>) => void;
}

interface OotItemProps {
  id: string;
  children: (props: OotItemChildProps) => ReactNode;
}

/**
 * Context bridge that connects individual items to the TrackerContext.
 * Replaces the old Redux-connected OotItem component.
 */
export function OotItem({ id, children }: OotItemProps) {
  const { state, dispatch, updateItem } = useTracker();

  const item = state.items.find((i) => i.id === id);

  // Auto-register item if not in state yet
  useEffect(() => {
    if (!item) {
      dispatch({
        type: 'SET_ITEMS',
        items: [...state.items, { id, isObtained: false, state: 0, isChecked: false }],
      });
    }
  }, [id, item, dispatch, state.items]);

  const handleUpdate = useCallback(
    (updates: Partial<ItemState>) => {
      updateItem(id, updates);
    },
    [id, updateItem]
  );

  // Provide a default while item is being registered
  const currentItem: ItemState = item ?? { id, isObtained: false, state: 0, isChecked: false };

  return <div className="flex">{children({ item: currentItem, updateItem: handleUpdate })}</div>;
}
